import axios from '../axios';
import * as types from './types';
import API from './api';
import { setMsgInfo } from './msgInfo';
import filterError from './common/filterError';

const baseUrl = `${API}/blog`;

// Blog Categories Actions
export const getBlogCategories = (query) => {
  return async (dispatch) => {
    dispatch({ type: types.GET_BLOG_CATEGORIES_LOADING });
    try {
      const queryString = query ? `?${new URLSearchParams(query).toString()}` : '';
      const { data } = await axios.get(`${baseUrl}/categories/${queryString}`);
      dispatch({ 
        type: types.GET_BLOG_CATEGORIES_SUCCESS, 
        payload: data 
      });
      return data;
    } catch (error) {
      const errors = filterError(error);
      dispatch({ 
        type: types.GET_BLOG_CATEGORIES_FAILURE, 
        payload: errors 
      });
      throw error;
    }
  };
};

export const createBlogCategory = (payload) => {
  return async (dispatch) => {
    try {
      const { data } = await axios.post(`${baseUrl}/categories/`, payload);
      dispatch({ 
        type: types.CREATE_BLOG_CATEGORY_SUCCESS, 
        payload: data 
      });
      // Refresh the categories list after creation
      dispatch(getBlogCategories());
      return data;
    } catch (error) {
      const errors = filterError(error);
      dispatch(
        setMsgInfo({
          success: false,
          msg: errors,
        })
      );
      throw error;
    }
  };
};

export const updateBlogCategory = (slug, payload) => {
  return async (dispatch) => {
    try {
      const { data } = await axios.patch(`${baseUrl}/categories/${slug}/`, payload);
      dispatch({ 
        type: types.UPDATE_BLOG_CATEGORY_SUCCESS, 
        payload: data 
      });
      // Refresh the categories list after update
      dispatch(getBlogCategories());
      return data;
    } catch (error) {
      const errors = filterError(error);
      dispatch(
        setMsgInfo({
          success: false,
          msg: errors,
        })
      );
      throw error;
    }
  };
};

export const deleteBlogCategory = (slug) => {
  return async (dispatch) => {
    try {
      await axios.delete(`${baseUrl}/categories/${slug}/`);
      dispatch({ 
        type: types.DELETE_BLOG_CATEGORY_SUCCESS, 
        payload: slug 
      });
      // Refresh the categories list after deletion
      dispatch(getBlogCategories());
    } catch (error) {
      const errors = filterError(error);
      dispatch(
        setMsgInfo({
          success: false,
          msg: errors,
        })
      );
      throw error;
    }
  };
};

// Blog Posts Actions
export const getBlogPosts = (query) => {
  return async (dispatch) => {
    dispatch({ type: types.GET_BLOG_POSTS_LOADING });
    try {
      const queryString = query ? `?${new URLSearchParams(query).toString()}` : '';
      const { data } = await axios.get(`${baseUrl}/posts/${queryString}`);
      dispatch({ 
        type: types.GET_BLOG_POSTS_SUCCESS, 
        payload: data 
      });
      return data;
    } catch (error) {
      const errors = filterError(error);
      dispatch({ 
        type: types.GET_BLOG_POSTS_FAILURE, 
        payload: errors 
      });
      throw error;
    }
  };
};

export const getBlogPostBySlug = (slug) => {
  return async (dispatch) => {
    dispatch({ type: types.GET_BLOG_POST_LOADING });
    try {
      const { data } = await axios.get(`${baseUrl}/posts/${slug}/`);
      dispatch({ 
        type: types.GET_BLOG_POST_SUCCESS, 
        payload: data 
      });
      return data;
    } catch (error) {
      const errors = filterError(error);
      dispatch({ 
        type: types.GET_BLOG_POST_FAILURE, 
        payload: errors 
      });
      throw error;
    }
  };
};

export const createBlogPost = (payload) => {
  return async (dispatch) => {
    try {
      const { data } = await axios.post(`${baseUrl}/posts/`, payload);
      dispatch({ 
        type: types.CREATE_BLOG_POST_SUCCESS, 
        payload: data 
      });
      return data;
    } catch (error) {
      const errors = filterError(error);
      dispatch(
        setMsgInfo({
          success: false,
          msg: errors,
        })
      );
      throw error;
    }
  };
};

export const updateBlogPost = (slug, payload) => {
  return async (dispatch) => {
    try {
      const { data } = await axios.patch(`${baseUrl}/posts/${slug}/`, payload);
      dispatch({ 
        type: types.UPDATE_BLOG_POST_SUCCESS, 
        payload: data 
      });
      return data;
    } catch (error) {
      const errors = filterError(error);
      dispatch(
        setMsgInfo({
          success: false,
          msg: errors,
        })
      );
      throw error;
    }
  };
};

export const deleteBlogPost = (slug) => {
  return async (dispatch) => {
    try {
      await axios.delete(`${baseUrl}/posts/${slug}/`);
      dispatch({ 
        type: types.DELETE_BLOG_POST_SUCCESS, 
        payload: slug 
      });
    } catch (error) {
      const errors = filterError(error);
      dispatch(
        setMsgInfo({
          success: false,
          msg: errors,
        })
      );
      throw error;
    }
  };
};

export const publishBlogPost = (slug) => {
  return async (dispatch) => {
    try {
      const { data } = await axios.post(`${baseUrl}/posts/${slug}/publish/`);
      dispatch({ 
        type: types.PUBLISH_BLOG_POST_SUCCESS, 
        payload: data 
      });
      return data;
    } catch (error) {
      const errors = filterError(error);
      dispatch(
        setMsgInfo({
          success: false,
          msg: errors,
        })
      );
      throw error;
    }
  };
};

export const unpublishBlogPost = (slug) => {
  return async (dispatch) => {
    try {
      const { data } = await axios.post(`${baseUrl}/posts/${slug}/unpublish/`);
      dispatch({ 
        type: types.UNPUBLISH_BLOG_POST_SUCCESS, 
        payload: data 
      });
      return data;
    } catch (error) {
      const errors = filterError(error);
      dispatch(
        setMsgInfo({
          success: false,
          msg: errors,
        })
      );
      throw error;
    }
  };
};

// Blog Comments Actions
export const getBlogComments = (postId, query) => {
  return async (dispatch) => {
    dispatch({ type: types.GET_BLOG_COMMENTS_LOADING });
    try {
      const queryString = query ? `?${new URLSearchParams(query).toString()}` : '';
      const { data } = await axios.get(`${baseUrl}/posts/${postId}/comments/${queryString}`);
      dispatch({ 
        type: types.GET_BLOG_COMMENTS_SUCCESS, 
        payload: data 
      });
      return data;
    } catch (error) {
      const errors = filterError(error);
      dispatch({ 
        type: types.GET_BLOG_COMMENTS_FAILURE, 
        payload: errors 
      });
      throw error;
    }
  };
};

export const createBlogComment = (postId, payload) => {
  return async (dispatch) => {
    try {
      const { data } = await axios.post(`${baseUrl}/posts/${postId}/comments/`, payload);
      dispatch({ 
        type: types.CREATE_BLOG_COMMENT_SUCCESS, 
        payload: data 
      });
      dispatch(
        setMsgInfo({
          success: true,
          msg: 'Comment posted successfully',
        })
      );
      return data;
    } catch (error) {
      const errors = filterError(error);
      dispatch(
        setMsgInfo({
          success: false,
          msg: errors,
        })
      );
      throw error;
    }
  };
};

export const updateBlogComment = (commentId, payload) => {
  return async (dispatch) => {
    try {
      const { data } = await axios.patch(`${baseUrl}/comments/${commentId}/`, payload);
      dispatch({ 
        type: types.UPDATE_BLOG_COMMENT_SUCCESS, 
        payload: data 
      });
      dispatch(
        setMsgInfo({
          success: true,
          msg: 'Comment updated successfully',
        })
      );
      return data;
    } catch (error) {
      const errors = filterError(error);
      dispatch(
        setMsgInfo({
          success: false,
          msg: errors,
        })
      );
      throw error;
    }
  };
};

export const deleteBlogComment = (commentId) => {
  return async (dispatch) => {
    try {
      await axios.delete(`${baseUrl}/comments/${commentId}/`);
      dispatch({ 
        type: types.DELETE_BLOG_COMMENT_SUCCESS, 
        payload: commentId 
      });
      dispatch(
        setMsgInfo({
          success: true,
          msg: 'Comment deleted successfully',
        })
      );
    } catch (error) {
      const errors = filterError(error);
      dispatch(
        setMsgInfo({
          success: false,
          msg: errors,
        })
      );
      throw error;
    }
  };
};

export const approveBlogComment = (commentId) => {
  return async (dispatch) => {
    try {
      const { data } = await axios.post(`${baseUrl}/comments/${commentId}/approve/`);
      dispatch({ 
        type: types.APPROVE_BLOG_COMMENT_SUCCESS, 
        payload: data 
      });
      dispatch(
        setMsgInfo({
          success: true,
          msg: 'Comment approved successfully',
        })
      );
      return data;
    } catch (error) {
      const errors = filterError(error);
      dispatch(
        setMsgInfo({
          success: false,
          msg: errors,
        })
      );
      throw error;
    }
  };
};

export const rejectBlogComment = (commentId) => {
  return async (dispatch) => {
    try {
      const { data } = await axios.post(`${baseUrl}/comments/${commentId}/reject/`);
      dispatch({ 
        type: types.REJECT_BLOG_COMMENT_SUCCESS, 
        payload: data 
      });
      dispatch(
        setMsgInfo({
          success: true,
          msg: 'Comment rejected successfully',
        })
      );
      return data;
    } catch (error) {
      const errors = filterError(error);
      dispatch(
        setMsgInfo({
          success: false,
          msg: errors,
        })
      );
      throw error;
    }
  };
};